import React from 'react';
import { fillFor } from '../wall/wallTokens';
import { T, RADIUS } from '../notifications/notificationModel';
import './kit.css';

/**
 * The account pages' kit (Settings · Wallet · Friends, Sept 2026 handoff): the same
 * portal vocabulary as Notifications — borderless white cards on the #F5F5F5 canvas,
 * pill controls, the ring frame for "on". Presentation only; nothing here fetches.
 * Handoff: ~/Downloads/design_handoff_settings_wallet_friends/README.md.
 */

export const A = {
  ...T,
  rowHover: '#FAFAFA',
  line: '#E5E7EB',
  verified: '#22C55E',
  moneyIn: '#16A34A',
  danger: '#DC2626',
  dangerFill: '#FEE2E2',
  dangerLine: '#FECACA',
  held: '#FAE1B4',
  escrow: '#FCAA6D',
};

export const AVATAR_TINTS = ['#A8D8EA', '#FFCCE6', '#C6B8FF', '#FAE1B4', '#00FF7F', '#B5B1FF', '#FCAA6D', '#22ECFB'];

export const MONO = "'JetBrains Mono', Menlo, monospace";

export const money = (cents) => `$${(Math.abs(cents || 0) / 100).toFixed(2)}`;

/** The title block: h1 + the 11px meta line. */
export function PageTitle({ title, meta, right, phone }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, paddingBottom: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h1 style={{ margin: 0, fontSize: phone ? 22 : 28, fontWeight: 700, letterSpacing: '.1em', lineHeight: 1 }}>{title}</h1>
        {meta && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11, letterSpacing: '.12em', color: A.grey500 }}>
            {meta}
          </span>
        )}
      </div>
      {right}
    </div>
  );
}

/** "LABEL ——————" with a 12px gap to whatever follows. */
export function SectionHead({ label, color = A.ink, right, size = 15 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <span style={{ fontSize: size, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap', color }}>{label}</span>
      {right}
      <span aria-hidden style={{ flex: 1, height: 2, background: color }} />
    </div>
  );
}

export function Card({ children, pad = 24, gap = 16, style, className = '', ...rest }) {
  return (
    <div
      className={className}
      style={{ display: 'flex', flexDirection: 'column', gap, padding: pad, borderRadius: RADIUS.panel, background: A.white, minWidth: 0, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

/** The 13px card label and its 12px grey line. */
export function CardTitle({ children, sub, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.12em' }}>{children}</span>
        {sub && <span style={{ fontSize: 12, color: A.grey500 }}>{sub}</span>}
      </div>
      {right}
    </div>
  );
}

export function Prose({ children, color = A.grey600, style }) {
  return <span style={{ fontSize: 12, color, lineHeight: 1.55, textWrap: 'pretty', ...style }}>{children}</span>;
}

const PILL_BASE = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: 40, padding: '0 18px', border: '2px solid transparent', borderRadius: RADIUS.pill,
  fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em',
  cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box',
  transition: 'background-color .2s, color .2s, opacity .2s', textDecoration: 'none',
};

const PILL_KIND = {
  primary: { background: A.ink, color: '#fff' },
  secondary: { background: A.off, color: A.ink },
  white: { background: A.white, color: A.ink },
  on: { background: fillFor('on'), color: '#fff' },
  danger: { background: A.danger, color: '#fff' },
  dangerSoft: { background: A.dangerFill, color: A.danger },
};

/**
 * A 40px pill. kind: primary · secondary · white · on · danger · dangerSoft.
 * `as="a"` (with href) or `as="span"` for a non-button; otherwise a real button.
 */
export function Pill({ kind = 'secondary', as, children, style, disabled, className = '', height, ...rest }) {
  const Tag = as || 'button';
  const props = Tag === 'button' ? { type: 'button', disabled } : { 'aria-disabled': disabled || undefined };
  return (
    <Tag
      className={`pr-acct-pill pr-acct-pill-${kind} ${className}`}
      style={{ ...PILL_BASE, ...PILL_KIND[kind], height: height ?? PILL_BASE.height, opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer', ...style }}
      {...props}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** 9px status pill: VERIFIED · NOT VERIFIED · PROCESSING … */
export function StatusPill({ children, bg = A.ink, fg = '#fff', style }) {
  return (
    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', padding: '4px 9px', borderRadius: RADIUS.pill, background: bg, color: fg, whiteSpace: 'nowrap', ...style }}>
      {children}
    </span>
  );
}

/** #F2F2F2 input, radius 16, transparent 2px border that turns black on focus. */
export const inputStyle = ({ height = 48, fontSize = 14, danger = false, ...extra } = {}) => ({
  height, padding: '0 18px', border: '2px solid transparent', borderRadius: 16, background: A.off,
  fontFamily: 'inherit', fontSize, color: A.ink, boxSizing: 'border-box', outline: 'none', minWidth: 0,
  ...extra,
});

export function Input({ danger = false, className = '', style, ...rest }) {
  return <input className={`pr-acct-input ${danger ? 'is-danger' : ''} ${className}`} style={{ ...inputStyle(), ...style }} {...rest} />;
}

/** A tiny label above an input / chip row. */
export function FieldLabel({ children, htmlFor, color = A.ink }) {
  return <label htmlFor={htmlFor} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color }}>{children}</label>;
}

export function Dot({ color = A.live, size = 8 }) {
  return <span aria-hidden style={{ width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />;
}

/** The empty card: a 13px title and a 12px grey line. */
export function EmptyCard({ title, body, pad = '48px 24px' }) {
  return (
    <div style={{ padding: pad, borderRadius: RADIUS.panel, background: A.white, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
      <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.14em' }}>{title}</span>
      {body && <span style={{ fontSize: 12, color: A.grey500 }}>{body}</span>}
    </div>
  );
}

/** 56px avatar (48 on phones), radius 14, an initial on a tint when there's no photo. */
export function Avatar({ src, name, tint, size = 56, radius = 14 }) {
  if (src) {
    return <img src={src} alt="" referrerPolicy="no-referrer" style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', display: 'block', flexShrink: 0 }} />;
  }
  return (
    <span aria-hidden style={{ width: size, height: size, borderRadius: radius, background: tint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size / 3), fontWeight: 800, flexShrink: 0 }}>
      {(String(name || '?').trim()[0] || '?').toUpperCase()}
    </span>
  );
}

export { fillFor, RADIUS };
